import { useRef } from "react";
import { Menu } from "primereact/menu";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { useGlobalContext } from "./Config";
import { useOffline } from "./offline/OfflineContext";
import {
  listarCotizacionesLocalesOffline,
  cotizacionTieneIdServidor,
} from "./offline/offlineCotizacionService";
import { syncCotizacionesPendientes } from "./offline/syncService";
import {
  canAccessSackBoleta,
  canAccessSackFactura,
  canManageUsers,
  canSynchronize,
  isEmpresaDinar,
  isEmpresaSack,
  isVendedor,
} from "./utilities/permissions";

const AppTopbar = (props) => {
  const navigate = useNavigate();
  const menuMantenedores = useRef(null);
  const menuReportes = useRef(null);
  const menuPerfil = useRef(null);
  const reportesTargetRef = useRef(null);
  const mantenedoresTargetRef = useRef(null);
  const perfilTargetRef = useRef(null);
  const hoverReportesTimeout = useRef(null);
  const hoverMantenedoresTimeout = useRef(null);
  const hoverPerfilTimeout = useRef(null);
  const { styleProperties, isDinar, isSack } = useGlobalContext();
  const { offlineMode, setOfflineMode } = useOffline();
  const canUseDinar = isDinar && isEmpresaDinar();
  const canUseSack = isSack && isEmpresaSack();

  const hideMenu = (menuRef, targetRef) => {
    if (menuRef?.current && targetRef?.current) {
      menuRef.current.hide({ currentTarget: targetRef.current });
    }
  };

  const closeAllMenus = () => {
    hideMenu(menuReportes, reportesTargetRef);
    hideMenu(menuMantenedores, mantenedoresTargetRef);
    hideMenu(menuPerfil, perfilTargetRef);
  };

  const openOnlyMenu = (menuRef, targetRef, currentTarget) => {
    targetRef.current = currentTarget;
    closeAllMenus();
    menuRef.current?.show({ currentTarget: targetRef.current });
  };

  const scheduleHideMenu = (menuRef, targetRef, timeoutRef, delay = 300) => {
    timeoutRef.current = setTimeout(() => {
      const targetEl = targetRef.current;
      const menuEl = menuRef.current?.getElement?.();
      const isTargetHovered = !!targetEl?.matches?.(":hover");
      const isMenuHovered = !!menuEl?.matches?.(":hover");

      if (!isTargetHovered && !isMenuHovered) {
        hideMenu(menuRef, targetRef);
      }
    }, delay);
  };

  const formatDateTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");

    return `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}`;
  };

  const handleOfflineSwitchChange = async (checked) => {
    const estabaOffline = offlineMode;
    setOfflineMode(checked);

    if (estabaOffline && !checked) {
      const cotizaciones = await listarCotizacionesLocalesOffline();

      const pendientes = cotizaciones.filter((item) => {
        if (cotizacionTieneIdServidor(item)) return false;

        return ["PENDIENTE", "ERROR", "ERROR_SYNC"].includes(
          item.estadoSync || "PENDIENTE"
        );
      });

      const ultimaSubida = cotizaciones
        .filter((item) => item.fechaSincronizacion)
        .sort(
          (a, b) =>
            new Date(b.fechaSincronizacion).getTime() -
            new Date(a.fechaSincronizacion).getTime()
        )[0];

      if (pendientes.length > 0) {
        const confirma = window.confirm(
          `Hay ${pendientes.length} cotización(es) pendiente(s) por subir.\n\n` +
            `Última subida: ${formatDateTime(
              ultimaSubida?.fechaSincronizacion
            )}\n\n` +
            `¿Desea sincronizar las cotizaciones ahora?`
        );

        if (confirma) {
          await syncCotizacionesPendientes();
        }
      }
    }
  };

  const itemsMantenedores = offlineMode
    ? [{ label: "Modo Offline", command: () => navigate("/offline") }]
    : [
        ...(canUseSack && canManageUsers()
          ? [
              {
                label: "Competencias",
                command: () => navigate("/competencias"),
                disabled: true,
              },
              {
                label: "Causas de Pérdida",
                command: () => navigate("/causas-perdida"),
                disabled: true,
              },
              {
                label: "Cierres de Cotización",
                command: () => navigate("/cierres-cotizacion"),
                disabled: true,
              },
              {
                label: "Estados",
                command: () => navigate("/estados"),
                disabled: true,
              },
              { label: "Usuarios", command: () => navigate("/usuarios") },
              {
                label: "Constantes P.E.",
                command: () => navigate("/constantes-pe"),
                disabled: true,
              },
              {
                label: "Materia Prima P.E.",
                command: () => navigate("/materia-prima-pe"),
                disabled: true,
              },
              {
                label: "Servicio de Armado P.E.",
                command: () => navigate("/servicio-armado-pe"),
                disabled: true,
              },
              {
                label: "Servicio de Corte P.E.",
                command: () => navigate("/servicio-corte-pe"),
                disabled: true,
              },
              {
                label: "Servicio de Perforacion P.E.",
                command: () => navigate("/servicio-perforacion-pe"),
                disabled: true,
              },
            ]
          : []),
        ...(canUseDinar
          ? [
              ...(canManageUsers()
                ? [{ label: "Usuarios", command: () => navigate("/usuarios") }]
                : []),
              ...(canSynchronize()
                ? [
                    {
                      label: "Sincronizar",
                      command: () => navigate("/sincronizar"),
                    },
                  ]
                : []),
            ]
          : []),
      ];

  const itemsReportes = offlineMode
    ? []
    : [
        ...(canUseSack
          ? [
              { label: "Tablero", command: () => navigate("/tablero") },
              {
                label: "Lista de Cotizaciones",
                command: () => navigate("/reporte-cotizacion"),
              },
              {
                label: "Estado de Cotizaciones",
                command: () => navigate("/estado-cotizaciones"),
                disabled: true,
              },
              {
                label: "Propuesta / Compra",
                command: () => navigate("/propuesta-compra"),
                disabled: true,
              },
              {
                label: "Estado Financiero",
                command: () => navigate("/estado-financiero"),
                disabled: true,
              },
              {
                label: "Coberturas",
                command: () => navigate("/coberturas"),
                disabled: true,
              },
              {
                label: "Reporte Precios Pendientes",
                command: () => navigate("/reporte-precios-pendientes"),
                disabled: true,
              },
            ]
          : []),
        ...(canUseDinar
          ? [
              {
                label: "Reportes",
                command: () => navigate("/reporte-cotizacion"),
              },
              {
                label: "Tiempo respuesta",
                command: () => navigate("/reporte-tiempo-respuesta"),
              },
              ...(!isVendedor()
                ? [
                    {
                      label: "Detalles no confirmados",
                      command: () => navigate("/reporte-no-confirmados"),
                    },
                  ]
                : []),
            ]
          : []),
      ];

  const itemsPerfil = [
    { label: "Administrar", command: () => navigate("/perfil") },
  ];

  return (
    <div className="layout-topbar shadow-4">
      <div className="layout-topbar-left p-0">
        <button
          type="button"
          style={{ cursor: "pointer" }}
          className=" p-link p-0"
          onClick={() => navigate("/tablero")}
        >
          <img
            src={styleProperties.logo}
            alt=""
            style={{
              height: "58px",
              objectFit: "contain",
              width: "10rem",
              backgroundColor: "white",
            }}
          />
        </button>

        <div className="topbar-offline-mobile lg:hidden" aria-label="Cambiar modo online u offline">
          <span className={offlineMode ? "is-offline" : "is-online"}>
            {offlineMode ? "OFF" : "ON"}
          </span>
          <InputSwitch
            checked={offlineMode}
            onChange={(e) => handleOfflineSwitchChange(e.value)}
          />
        </div>

        <button
          type="button"
          className="layout-menu-button shadow-6 p-link lg:hidden"
          onClick={props.onMenuButtonClick}
        >
          <i className="pi pi-chevron-right"></i>
        </button>
      </div>

      <div className="layout-topbar-right p-0">
        <div className="flex align-items-center justify-content-between w-full">
          <div className="flex align-items-center">
            {canUseDinar && (
              <Button
                type="button"
                className="p-button-text text-white font-medium"
                onClick={() => navigate("/nota-venta")}
                label="Ingresar Nota De Venta"
                icon="pi pi-file"
              />
            )}

            {canUseSack && (
              <>
                {canAccessSackBoleta() && (
                  <Button
                    type="button"
                    className="p-button-text text-white font-medium"
                    onClick={() => navigate("/boleta")}
                    label="Cotización Boleta"
                    icon="pi pi-file"
                  />
                )}

                {canAccessSackFactura() && (
                  <Button
                    type="button"
                    className="p-button-text text-white font-medium"
                    onClick={() => navigate("/factura")}
                    label="Cotización Factura"
                    icon="pi pi-file-o"
                  />
                )}

                {!offlineMode && (
                  <Button
                    type="button"
                    className="p-button-text text-white font-medium"
                    onClick={() => navigate("/reporte-cotizacion")}
                    label="Cotizaciones"
                    icon="pi pi-folder"
                  />
                )}
              </>
            )}

            {!offlineMode && (
              <>
                <Menu
                  model={itemsReportes}
                  popup
                  ref={menuReportes}
                  id="popup_menu_reportes"
                />

                <Button
                  type="button"
                  className="p-button-text text-white font-medium"
                  onClick={(event) => menuReportes.current.toggle(event)}
                  onMouseEnter={(event) => {
                    if (hoverReportesTimeout.current) {
                      clearTimeout(hoverReportesTimeout.current);
                      hoverReportesTimeout.current = null;
                    }
                    openOnlyMenu(
                      menuReportes,
                      reportesTargetRef,
                      event.currentTarget
                    );
                  }}
                  onMouseLeave={() => {
                    scheduleHideMenu(
                      menuReportes,
                      reportesTargetRef,
                      hoverReportesTimeout
                    );
                  }}
                  aria-controls="popup_menu_reportes"
                  aria-haspopup
                  label="Reportes"
                  icon="pi pi-chart-bar"
                />
              </>
            )}
            {!offlineMode && itemsMantenedores.length > 0 && (
              <>
                <Menu
                  model={itemsMantenedores}
                  popup
                  ref={menuMantenedores}
                  id="popup_menu_mantenedores"
                />

                <Button
                  type="button"
                  className="p-button-text text-white font-medium"
                  onClick={(event) => menuMantenedores.current.toggle(event)}
                  onMouseEnter={(event) => {
                    if (hoverMantenedoresTimeout.current) {
                      clearTimeout(hoverMantenedoresTimeout.current);
                      hoverMantenedoresTimeout.current = null;
                    }
                    openOnlyMenu(
                      menuMantenedores,
                      mantenedoresTargetRef,
                      event.currentTarget
                    );
                  }}
                  onMouseLeave={() => {
                    scheduleHideMenu(
                      menuMantenedores,
                      mantenedoresTargetRef,
                      hoverMantenedoresTimeout
                    );
                  }}
                  aria-controls="popup_menu_mantenedores"
                  aria-haspopup
                  label="Mantenedores"
                  icon="pi pi-cog"
                />
              </>
            )}

            <Button
              type="button"
              className="p-button-text text-white font-medium"
              onClick={() => navigate("/offline")}
              label="Modo Offline"
              icon="pi pi-database"
            />

            {(canUseDinar || canUseSack) && (
              <>
                {!offlineMode && (
                  <>
                    <Menu
                      model={itemsPerfil}
                      popup
                      ref={menuPerfil}
                      id="popup_menu_perfil"
                    />

                    <Button
                      type="button"
                      className="p-button-text text-white font-medium"
                      label="Perfil"
                      icon="pi pi-user"
                      onClick={(event) => menuPerfil.current.toggle(event)}
                      onMouseEnter={(event) => {
                        if (hoverPerfilTimeout.current) {
                          clearTimeout(hoverPerfilTimeout.current);
                          hoverPerfilTimeout.current = null;
                        }
                        openOnlyMenu(
                          menuPerfil,
                          perfilTargetRef,
                          event.currentTarget
                        );
                      }}
                      onMouseLeave={() => {
                        scheduleHideMenu(
                          menuPerfil,
                          perfilTargetRef,
                          hoverPerfilTimeout
                        );
                      }}
                      aria-controls="popup_menu_perfil"
                      aria-haspopup
                    />
                  </>
                )}

                <Button
                  type="button"
                  className="p-button-text text-white"
                  onClick={() => {
                    sessionStorage.clear();
                    if (offlineMode) {
                      navigate("/");
                      return;
                    }
                    const loginUrl =
                      process.env.REACT_APP_SSO_LOGIN_URL ||
                      "https://sso.kesp.cl/sso/login.htm";
                    window.location.href = `${loginUrl}?logout`;
                  }}
                  label="Salir"
                  icon="pi pi-power-off"
                />
              </>
            )}
          </div>

          <div
            className="topbar-offline-desktop flex align-items-center mr-4"
            style={{ gap: ".5rem" }}
          >
            <span
              style={{
                fontWeight: 600,
                color: offlineMode ? "#ffb300" : "white",
              }}
            >
              {offlineMode ? "OFFLINE" : "ONLINE"}
            </span>

            <InputSwitch
              checked={offlineMode}
              onChange={(e) => handleOfflineSwitchChange(e.value)}
            />
          </div>

          <div className="flex align-items-center gap-1 mr-3">
            <span className="">{sessionStorage.getItem("user")}</span>

            <span className="">| Ver.2.0.4 </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppTopbar;
